/**
 * FloorplanPersona - Custom persona for floorplan workflow
 *
 * Extends BasePersona to add floor selection state tracking.
 * Manages conditional tool availability based on floor selection.
 */

import { BasePersona } from '../../BasePersona';
import { DetectedFloorPlane } from '../../../utils/floorDetection';
import { fetchFloorPlanes, subscribeToFloorPlaneProperties } from '../../../utils/FloorIsolation';
import type { Subscription } from '@pointorama/potree-shared';
import { FloorplanToolBase } from '../../tools/implementations/FloorplanToolBase';
import type { AnnotationBlacklist } from '../../../viewer/AnnotationBlacklistStack';
import type { IPersonaMode } from '../../types';

/** The two values of the `floor_wall_source` floor property. */
export type WallSourceChoice = 'ROOMS' | 'WALL_SKETCH';

export interface FloorGroupData {
  groupIdentifier: string;
  name: string;
  floor: DetectedFloorPlane;
  ceiling?: DetectedFloorPlane;
  /** The floor's own persisted XY footprint corners, if stored. */
  footprintCorners?: Array<{ x: number; y: number; z: number }>;
}

interface FloorSelectionEvent {
  selectedFloorGroupIdentifier: string | null;
  floorData: {
    floor: DetectedFloorPlane;
    ceiling?: DetectedFloorPlane | null;
    roomIdentifiers?: string[];
  } | null;
}

interface ObjectsDeletedEvent {
  deletedGroupIdentifiers: string[];
}

interface ObjectsChangedEvent {
  groups: { type: string }[];
  floorGroupsData: FloorGroupData[];
}

export class FloorplanPersona extends BasePersona {
  private hasActiveFloor = false;
  private floorsExist = false;

  /**
   * Hand-off slot for the door/window wall focus when switching between the
   * magic and manual door/window tools. The outgoing tool stashes the active
   * wall spec here (instead of tearing the focus down) when the incoming tool
   * is the sibling door/window tool; the incoming tool restores phase-2 on the
   * same wall + view. Typed loosely to avoid coupling the persona to the tool's
   * WallFocusSpec / RenderViewState modules.
   *
   * `widget` carries a full RenderViewState snapshot of the widget at hand-off
   * so the user's panned/orbited 3D wall view survives the swap. Null when no
   * widget is mounted.
   */
  private pendingDoorWindowWallFocus: { spec: unknown; camera: unknown; widget: unknown } | null = null;

  setPendingDoorWindowWallFocus(spec: unknown, camera: unknown, widget: unknown): void {
    this.pendingDoorWindowWallFocus = { spec, camera, widget };
  }

  /** Read and clear the pending wall focus + camera (one-shot hand-off). */
  takePendingDoorWindowWallFocus(): { spec: unknown; camera: unknown; widget: unknown } | null {
    const pending = this.pendingDoorWindowWallFocus;
    this.pendingDoorWindowWallFocus = null;
    return pending;
  }

  /** The tool id replacing the current one during an exclusive switch, or null. */
  getIncomingToolId(): string | null {
    return this.incomingToolId;
  }

  // Floor data storage - single source of truth
  private floorGroupsData: FloorGroupData[] = [];
  private selectedFloorGroupIdentifier: string | null = null;
  private selectedFloorData: {
    floor: DetectedFloorPlane;
    ceiling: DetectedFloorPlane | null;
    roomIdentifiers: string[];
  } | null = null;

  /** Live subscriptions to the selected floor's reactive plane properties,
   *  so dimension edits refresh `selectedFloorData` and re-notify tools. */
  private floorPlaneSubscriptions: Subscription[] = [];
  /** Floor group the plane subscriptions are attached to. */
  private floorPlaneSubscriptionGroupId: string | null = null;

  /** Floors already prompted for a wall source this Wall-tab stay — cleared on
   *  leaving the tab, so the prompt re-appears on the next entry if the floor
   *  is still sketchless. */
  private promptedWallSourceFloors = new Set<string>();
  /** Floor the currently open wall-source prompt targets, or null. */
  private wallSourcePromptFloorId: string | null = null;

  /**
   * Override enable to set up floor selection listeners
   */
  protected onEnable(): void {
    super.onEnable();

    this.viewer.floorIsolationManager.enterCustomMode();

    // Listen for objects changed, selection changed, and deletion events
    this.viewer.scene.addEventListener('objects_changed', this.handleObjectsChanged);
    this.viewer.scene.addEventListener('objects_selection_changed', this.handleSelectionChanged);
    this.viewer.scene.addEventListener('objects_deleted', this.handleObjectsDeleted);
    // Wall sketch lifecycle gates the design-tab tools (`requiresWallSketch`).
    this.viewer.scene.addEventListener('wall_sketch_added', this.handleWallSketchLifecycle);
    this.viewer.scene.addEventListener('wall_sketch_removed', this.handleWallSketchLifecycle);

    // Emit initial state (tools disabled by default until floor is selected)
    this.updateToolAvailability();

    // Apply the persona's baseline visibility filter (no floor selected yet).
    // For the base floorplan persona this is a no-op (null); subclasses like
    // LayoutPersona use it to hide non-sketch annotation types immediately.
    this.isolateFloorVisibility();

    // Request current objects list from client after listeners are set up
    setTimeout(() => {
      this.viewer.scene.dispatchEvent({
        type: 'objects_request',
        value: {},
      });
    }, 0);
  }

  /**
   * Override disable to clean up listeners
   */
  protected onDisable(): void {
    this.viewer.scene.removeEventListener('objects_changed', this.handleObjectsChanged);
    this.viewer.scene.removeEventListener('objects_selection_changed', this.handleSelectionChanged);
    this.viewer.scene.removeEventListener('objects_deleted', this.handleObjectsDeleted);
    this.viewer.scene.removeEventListener('wall_sketch_added', this.handleWallSketchLifecycle);
    this.viewer.scene.removeEventListener('wall_sketch_removed', this.handleWallSketchLifecycle);

    this.viewer.floorIsolationManager.exitCustomMode();

    this.clearFloorPlaneSubscriptions();

    // Reset state
    this.hasActiveFloor = false;
    this.floorsExist = false;

    this.promptedWallSourceFloors.clear();
    this.closeWallSourcePrompt();

    this.restoreFloorVisibility();

    super.onDisable();
  }

  /**
   * Override setMode to switch tools while preserving the selected floor.
   * The active floor persists across mode switches so tools like ManualRoomDrawTool
   * know which floor to create rooms on.
   */
  setMode(modeId: string): void {
    super.setMode(modeId);
  }

  /**
   * Override onModeChanged to update tool availability when switching modes.
   * Since hasActiveFloor state is already tracked, tools can check availability synchronously.
   */
  protected onModeChanged(modeId: string, _mode: IPersonaMode): void {
    this.updateToolAvailability();
    if (modeId === 'wall') {
      this.maybePromptWallSource();
    } else {
      // Leaving the Wall tab: the once-per-floor memory resets (prompt again
      // on the next entry if still sketchless) and any open prompt closes.
      this.promptedWallSourceFloors.clear();
      this.closeWallSourcePrompt();
    }
  }

  /** Tool deactivation re-evaluates the wall-source prompt (it only shows
   *  while the create-walls tool is active). */
  override disableTool(name: string): void {
    super.disableTool(name);
    this.maybePromptWallSource();
  }

  // ==================== Wall-source mode prompt ====================

  /**
   * Whether the selected floor should be asked for its wall source: its wall
   * sketch has no content AT ALL (no wall edges and no merged outline edges),
   * so the user hasn't committed to either the Measured or the Wall-sketch
   * workflow. Note the difference to {@link hasNonEmptyWallSketch}: a
   * Measured floor whose sketch holds only outline ring edges must NOT be
   * re-prompted, while the design-tab tool gating still requires actual
   * walls. A floor without any measured geometry (rooms/outlines) is never
   * asked either — "as measured" has nothing to measure from, so the
   * property default (WALL_SKETCH) stands and the user just draws walls.
   */
  private floorNeedsWallSourcePrompt(): boolean {
    const floorId = this.selectedFloorGroupIdentifier;
    if (!floorId) return false;
    const hasMeasuredGeometry =
      this.viewer.scene.rooms.some((r) => r.groupIdentifier === floorId) || this.viewer.scene.outlines.some((o) => (o as { groupIdentifier?: string }).groupIdentifier === floorId);
    if (!hasMeasuredGeometry) return false;
    const sketch = this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorId);
    return !sketch || sketch.getAllEdges().length === 0;
  }

  /**
   * Show (or retire) the "Measured or Wall sketch?" prompt for the selected
   * floor. Re-evaluated on tab entry, floor switches and wall-sketch
   * lifecycle events: a floor that stops qualifying (sketch landed — own
   * commit or cross-tab sync; floor deselected) auto-dismisses; each floor is
   * prompted at most once per Wall-tab stay.
   */
  private maybePromptWallSource(): void {
    if (this.getCurrentMode()?.id !== 'wall') return;
    // The prompt belongs to the create-walls tool: it only shows while that
    // tool is active, and closes when any other tool takes over.
    if (!this.tools.get('wall_create')?.enabled) {
      this.closeWallSourcePrompt();
      return;
    }
    const floorId = this.selectedFloorGroupIdentifier;
    if (!floorId || !this.floorNeedsWallSourcePrompt()) {
      this.closeWallSourcePrompt();
      return;
    }
    if (this.wallSourcePromptFloorId === floorId) return; // already open for it
    if (this.promptedWallSourceFloors.has(floorId)) {
      this.closeWallSourcePrompt();
      return;
    }
    this.promptedWallSourceFloors.add(floorId);
    this.wallSourcePromptFloorId = floorId;
    this.dispatchEvent({ type: 'wall_source_prompt', floorGroupIdentifier: floorId });
  }

  /** Close the open prompt (if any) without writing anything. */
  private closeWallSourcePrompt(): void {
    if (this.wallSourcePromptFloorId === null) return;
    this.wallSourcePromptFloorId = null;
    this.dispatchEvent({ type: 'wall_source_prompt_dismiss' });
  }

  /** The prompt's answer: write the floor's `floor_wall_source` property via
   *  the representation manager (the single existing write path). */
  chooseWallSource(source: WallSourceChoice): void {
    const floorId = this.wallSourcePromptFloorId;
    this.wallSourcePromptFloorId = null;
    if (!floorId) return;
    this.viewer.floorplanRepresentation?.setModeForFloor(floorId, source === 'ROOMS' ? 'ORIGINAL' : 'SKINNED');
  }

  /** Prompt dismissed (backdrop / Esc / X): no write — the property default
   *  (WALL_SKETCH) stands; no re-prompt until the next Wall-tab entry. */
  dismissWallSourcePrompt(): void {
    this.wallSourcePromptFloorId = null;
  }

  /**
   * Handle selection changed event
   */
  private handleSelectionChanged = (event: FloorSelectionEvent): void => {
    const selectedFloorGroupIdentifier = event.selectedFloorGroupIdentifier;
    let floorData = event.floorData;

    // Floor selection is STICKY: selecting a non-floor object (measurement,
    // annotation…) emits a null floor id, and the active floor must survive
    // it so tools keep working. Explicit deselection goes through
    // clearFloorSelection() instead.
    if (!selectedFloorGroupIdentifier) {
      return;
    }

    // If the event doesn't carry floor data (e.g. property manager hasn't re-rendered yet),
    // try to look it up from our own stored floorGroupsData (populated by objects_changed).
    if (!floorData) {
      const stored = this.floorGroupsData.find((f) => f.groupIdentifier === selectedFloorGroupIdentifier);
      if (stored) {
        floorData = { floor: stored.floor, ceiling: stored.ceiling, roomIdentifiers: [] };
      }
    }

    // Store selection state — floorData may be null for newly created floors
    // whose properties haven't loaded yet, but the floor is still active
    this.selectedFloorGroupIdentifier = selectedFloorGroupIdentifier;
    if (floorData) {
      this.selectedFloorData = {
        floor: floorData.floor,
        ceiling: floorData.ceiling || null,
        roomIdentifiers: floorData.roomIdentifiers || [],
      };
    }

    // Track the selected floor's plane properties so dimension edits propagate.
    this.updateFloorPlaneSubscriptions();

    // A floor is active when selected, even if plane data hasn't loaded yet
    const hadActiveFloor = this.hasActiveFloor;
    this.hasActiveFloor = true;

    // Only update tool availability when a NEW floor is selected
    if (!hadActiveFloor) {
      // Floor was selected - update tool availability
      this.updateToolAvailability();
    }

    // Notify tools about floor selection change
    this.dispatchEvent({
      type: 'floor_selection_changed',
      selectedFloorGroupIdentifier: this.selectedFloorGroupIdentifier,
      floorData: this.selectedFloorData,
    });

    // Temporarily hide objects not on the selected floor
    this.isolateFloorVisibility();

    // Floor switch while in the Wall tab: re-evaluate the wall-source prompt
    // (dismisses for the old floor, prompts for the new one when sketchless).
    this.maybePromptWallSource();
  };

  /**
   * EXPLICIT floor deselection (e.g. a void click in the manual floor tool).
   * Clears the sticky selection and notifies tools so highlights, isolation
   * and the floor bars reset to the unselected state. Plain selection-changed
   * events with a null floor id deliberately do NOT do this — see
   * handleSelectionChanged.
   */
  clearFloorSelection(): void {
    if (!this.selectedFloorGroupIdentifier) return;
    this.selectedFloorGroupIdentifier = null;
    this.selectedFloorData = null;
    this.hasActiveFloor = false;
    this.clearFloorPlaneSubscriptions();
    this.updateToolAvailability();
    this.dispatchEvent({
      type: 'floor_selection_changed',
      selectedFloorGroupIdentifier: null,
      floorData: null,
    });
    this.restoreFloorVisibility();
    this.closeWallSourcePrompt();
  }

  /**
   * Handle objects deleted event
   */
  private handleObjectsDeleted = (event: ObjectsDeletedEvent): void => {
    const deletedGroupIdentifiers = event.deletedGroupIdentifiers || [];

    // Check if the currently selected floor was deleted
    if (this.selectedFloorGroupIdentifier && deletedGroupIdentifiers.includes(this.selectedFloorGroupIdentifier)) {
      // Clear selection state
      this.selectedFloorGroupIdentifier = null;
      this.selectedFloorData = null;
      this.hasActiveFloor = false;
      this.clearFloorPlaneSubscriptions();

      // Update tool availability (will switch to floor_selector tool)
      this.updateToolAvailability();

      this.restoreFloorVisibility();

      // The prompt (if open) targeted the deleted floor.
      this.closeWallSourcePrompt();
    }
  };

  /**
   * Handle objects changed event to check for floor existence and store floor data
   */
  private handleObjectsChanged = (event: ObjectsChangedEvent): void => {
    const groups = event.groups || [];
    const floorGroupsData = event.floorGroupsData || [];

    // Store floor groups data
    this.floorGroupsData = floorGroupsData;

    // Check if any floor groups exist (type 'Floor' or however they're identified)
    const floorGroupsExist = groups.some((group) => group.type === 'FLOOR');

    if (this.floorsExist !== floorGroupsExist) {
      this.floorsExist = floorGroupsExist;
      this.updateToolAvailability();
    }

    // If a floor is selected but its data wasn't available yet (e.g. newly created floor
    // whose properties were saved after the group appeared in cache), update now and re-isolate.
    if (this.selectedFloorGroupIdentifier && !this.selectedFloorData) {
      const matchingFloor = floorGroupsData.find((f) => f.groupIdentifier === this.selectedFloorGroupIdentifier);
      if (matchingFloor) {
        this.selectedFloorData = {
          floor: matchingFloor.floor,
          ceiling: matchingFloor.ceiling || null,
          roomIdentifiers: [],
        };

        // Notify tools about floor selection change with the now-available data
        this.dispatchEvent({
          type: 'floor_selection_changed',
          selectedFloorGroupIdentifier: this.selectedFloorGroupIdentifier,
          floorData: this.selectedFloorData,
        });

        this.isolateFloorVisibility();
      }
    }

    // Retry in case the property service wasn't available when the floor was selected.
    this.updateFloorPlaneSubscriptions();

    // Notify tools about floor data change
    this.dispatchEvent({
      type: 'floor_data_changed',
      floorGroupsData: this.floorGroupsData,
    });
  };

  // ==================== Reactive floor dimensions ====================

  /**
   * Keep `selectedFloorData` in sync with the selected floor's reactive plane
   * properties. Dimension edits (floor/ceiling elevation, height) write to
   * those properties; without a subscription the stored snapshot goes stale
   * and tools keep clipping and outlining at the old heights.
   */
  private updateFloorPlaneSubscriptions(): void {
    const floorId = this.selectedFloorGroupIdentifier;
    if (floorId === this.floorPlaneSubscriptionGroupId) return;
    this.clearFloorPlaneSubscriptions();
    if (!floorId) return;
    const subscriptions = subscribeToFloorPlaneProperties(floorId, this.handleFloorPlanesChanged);
    if (subscriptions.length > 0) {
      this.floorPlaneSubscriptions = subscriptions;
      this.floorPlaneSubscriptionGroupId = floorId;
    }
  }

  private clearFloorPlaneSubscriptions(): void {
    for (const subscription of this.floorPlaneSubscriptions) {
      subscription.unsubscribe();
    }
    this.floorPlaneSubscriptions = [];
    this.floorPlaneSubscriptionGroupId = null;
  }

  private handleFloorPlanesChanged = (): void => {
    const floorId = this.selectedFloorGroupIdentifier;
    if (!floorId) return;
    const planes = fetchFloorPlanes(floorId);
    if (!planes) return;

    // fetchFloorPlanes already filters unset {0,0,0} ceilings via isMeaningfulCeiling.
    this.selectedFloorData = {
      floor: planes.floor,
      ceiling: planes.ceiling,
      roomIdentifiers: this.selectedFloorData?.roomIdentifiers || [],
    };

    // Re-emit the selection with the refreshed planes so tools re-apply
    // isolation clips and preview outlines at the new heights.
    this.dispatchEvent({
      type: 'floor_selection_changed',
      selectedFloorGroupIdentifier: floorId,
      floorData: this.selectedFloorData,
    });
  };

  /**
   * Hide every annotation that isn't part of the selected floor by pushing a
   * per-view whitelist onto every render view (main + widget). No scene
   * objects are mutated, so the user's own visibility toggles stay
   * authoritative. The filter is per-RenderView, so each view needs its own.
   */
  protected isolateFloorVisibility(): void {
    const floorId = this.selectedFloorGroupIdentifier;
    const bl = this.buildVisibilityBlacklist(floorId);
    for (const view of this.viewer.renderViews) {
      view.viewState?.setAnnotationBlacklist(bl);
    }
  }

  /**
   * Build the per-view annotation blacklist for the current floor selection.
   * Base behaviour: whitelist the selected floor group (everything tagged with
   * it stays visible, everything else hidden); no filter when no floor is
   * selected. Subclasses (e.g. LayoutPersona) override to add type constraints.
   */
  protected buildVisibilityBlacklist(floorId: string | null): AnnotationBlacklist | null {
    if (!floorId) return null;
    return { mode: 'whitelist', identifiers: new Set([floorId]) };
  }

  /**
   * Reset the floor-isolation blacklist on every render view. Subclasses can
   * override to keep a persona-level baseline filter (e.g. always hiding some
   * annotation types) instead of clearing entirely.
   */
  protected restoreFloorVisibility(): void {
    for (const view of this.viewer.renderViews) {
      view.viewState?.setAnnotationBlacklist(this.buildVisibilityBlacklist(null));
    }
  }

  /**
   * Update tool availability based on floor selection state and floor existence
   * Disables tools that extend FloorplanToolBase when no floor is selected
   * Disables floorplan mode when no floors exist
   */
  private updateToolAvailability(): void {
    // Get all tools that require a floor (extend FloorplanToolBase)
    const floorplanTools = Array.from(this.tools.entries()).filter((entry): entry is [string, FloorplanToolBase] => entry[1] instanceof FloorplanToolBase);

    // If the active tool lost its prerequisites (no floor selected, or a
    // wall-sketch-requiring tool whose sketch is gone), switch back to the
    // mode's default selection tool. Each mode names its selector differently
    // (floor_selector, room_selection, wall_selection, design_selection),
    // so try them in order.
    const activeToolLostPrereq = floorplanTools.find(([id, tool]) => {
      if (!tool.enabled || id === 'floor_selector' || id.endsWith('_selection')) return false;
      if (!this.hasActiveFloor) return tool.requiresFloor;
      return this.toolRequiresWallSketch(id) && !this.hasWallSourceGeometry();
    });
    if (activeToolLostPrereq) {
      const selectorId = ['floor_selector', 'room_selection', 'wall_selection', 'design_selection'].find((id) => this.tools.has(id));
      if (selectorId) {
        this.enableTool(selectorId);
      }
    }

    // Dispatch event to notify UI about tool availability changes
    // hasActiveFloor is true only when floors exist AND one is selected
    this.dispatchEvent({
      type: 'tool_availability_changed',
      toolsRequiringFloor: floorplanTools.filter(([, tool]) => tool.requiresFloor).map(([name]) => name),
      hasActiveFloor: this.hasActiveFloor,
      floorsExist: this.floorsExist,
    });
  }

  /** Wall sketches appearing/disappearing re-gates the `requiresWallSketch`
   *  tools (design tab) — recompute availability so the toolbar re-queries.
   *  Also re-evaluates the wall-source prompt (a sketch landing for the
   *  prompted floor auto-dismisses it; a removal may prompt). */
  private handleWallSketchLifecycle = (event: { type: string; groupIdentifier?: string }): void => {
    this.updateToolAvailability();
    // Deleting a sketch is a deliberate act on a floor whose representation
    // the user already chose — don't re-ask "how should the walls be
    // represented?" the moment the floor turns sketchless again. Marking the
    // floor prompted suppresses the ask until the next Wall-tab entry.
    if (event.type === 'wall_sketch_removed') {
      const floorId = event.groupIdentifier ?? this.selectedFloorGroupIdentifier;
      if (floorId) this.promptedWallSourceFloors.add(floorId);
    }
    this.maybePromptWallSource();
  };

  /** True when the selected floor has a wall sketch with at least one edge —
   *  an EMPTY sketch annotation (bare `request_skeleton`) doesn't count. */
  protected hasNonEmptyWallSketch(): boolean {
    const floorId = this.selectedFloorGroupIdentifier;
    if (!floorId) return false;
    const sketch = this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorId);
    return !!sketch && sketch.getEdges().length > 0;
  }

  /**
   * True when the selected floor has ANY geometry the design tools can place
   * openings on: sketch walls always; rooms/outlines additionally count on
   * MEASURED-source floors, where the door/window tools fall back to
   * room/outline-edge picking and materialize a loose sketch edge on click
   * (see DoorWindowTool.focusWallFromRoomPick). In the standardised
   * (wall-sketch) mode the sketch is authoritative, so rooms alone don't
   * unlock the tools.
   */
  protected hasWallSourceGeometry(): boolean {
    if (this.hasNonEmptyWallSketch()) return true;
    const floorId = this.selectedFloorGroupIdentifier;
    if (!floorId) return false;
    if (!(this.viewer.floorplanRepresentation?.isOriginalActive(floorId) ?? false)) return false;
    return this.viewer.scene.rooms.some((r) => r.groupIdentifier === floorId) || this.viewer.scene.outlines.some((o) => (o as { groupIdentifier?: string }).groupIdentifier === floorId);
  }

  /** Whether the current mode's config flags `toolName` with `requiresWallSketch`. */
  private toolRequiresWallSketch(toolName: string): boolean {
    const mode = this.getCurrentMode();
    if (!mode) return false;
    return this.extractToolConfigs(mode).find((t) => t.id === toolName)?.requiresWallSketch === true;
  }

  /**
   * Override enableTool to prevent enabling tools that require a floor
   * (or a wall sketch) and to request current floor selection state on tool switch
   */
  enableTool(toolName: string): void {
    // Check if tool requires a floor (extends FloorplanToolBase) and none is selected
    const tool = this.tools.get(toolName);
    if (!this.hasActiveFloor && tool instanceof FloorplanToolBase && tool.requiresFloor) {
      // Silently ignore the request (tool remains disabled)
      return;
    }
    if (this.toolRequiresWallSketch(toolName) && !this.hasWallSourceGeometry()) {
      // Silently ignore — the design tools need SOME wall source (sketch
      // walls, rooms, or outlines) to place openings on.
      return;
    }

    // Otherwise, proceed with normal enableTool logic
    super.enableTool(toolName);

    // Tool switches re-evaluate the wall-source prompt: activating the
    // create-walls tool may pop it, activating anything else closes it.
    this.maybePromptWallSource();
  }

  /**
   * Check if a tool is available (not disabled due to missing requirements)
   */
  isToolAvailable(toolName: string): boolean {
    const tool = this.tools.get(toolName);
    if (tool instanceof FloorplanToolBase) {
      if (!this.hasActiveFloor && tool.requiresFloor) return false;
      if (this.toolRequiresWallSketch(toolName) && !this.hasWallSourceGeometry()) return false;
      return true;
    }
    return true;
  }

  // ==================== Floor Data Accessors ====================

  /**
   * Get all floor groups data
   */
  getFloorGroupsData(): FloorGroupData[] {
    return this.floorGroupsData;
  }

  /**
   * Get the currently selected floor group identifier
   */
  getSelectedFloorGroupIdentifier(): string | null {
    return this.selectedFloorGroupIdentifier;
  }

  /**
   * Get the currently selected floor data (floor and ceiling planes, and room identifiers)
   */
  getSelectedFloorData(): {
    floor: DetectedFloorPlane;
    ceiling: DetectedFloorPlane | null;
    roomIdentifiers: string[];
  } | null {
    return this.selectedFloorData;
  }

  /**
   * Check if a floor is currently selected
   */
  hasFloorSelected(): boolean {
    return this.hasActiveFloor;
  }

  /**
   * Check if any floors exist in the project
   */
  doFloorsExist(): boolean {
    return this.floorsExist;
  }
}
